import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { formatDate } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { Pagination } from '../components/people/Pagination';
import { GatedSection } from '../components/shared/GatedSection';
import { EditIconButton } from '../components/shared/EditIconButton';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import {
  FilterBar,
  SortableHeader,
  useListFilters,
  type FilterRoute,
} from '../components/filters';
import {
  assessmentsFilterConfig,
  assessmentsSearchSchema,
  ASSESSMENTS_SORT_COLUMNS,
} from '../components/assessments/assessmentsFilterConfig';
import {
  ModeChip,
  OwnerTypeChip,
  ScopePicker,
  ScopeChip,
  StatusChip,
  TemplateDrawer,
  canAuthorAtScope,
  scopeTabsForRole,
  SCOPE_TAB_LABEL,
  type AssessmentScope,
  type TemplateListItem,
  type TemplateDrawerMode,
} from '../components/assessments';

export const Route = createFileRoute('/_authed/academics/assessments')({
  validateSearch: assessmentsSearchSchema,
  component: AssessmentTemplatesPage,
});

interface ListResponse {
  items: TemplateListItem[];
  total: number;
  page: number;
  pageSize: number;
}

const PAGE_SIZE = 20;

const ALL_SCOPES: ReadonlyArray<AssessmentScope> = [
  'hiring',
  'rules',
  'weekly_quiz',
  'monthly_quiz',
  'interview_script',
];

function AssessmentTemplatesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const role = useAuthStore((s) => s.user?.primaryRole);

  const [scopePickerOpen, setScopePickerOpen] = useState(false);
  const [drawer, setDrawer] = useState<TemplateDrawerMode | null>(null);

  // The single source of truth for search / filters / sort / page, backed by
  // the route's URL search params. The kind-of-test tabs (and older links)
  // use `?scope=weekly_quiz` — that single value decodes into the scope
  // facet's selected values and renders as a pill (see
  // `assessmentsFilterConfig`), so the prefilter needs no bespoke handling. The adapter
  // narrows the route's precisely-typed search/navigate surface to the
  // toolkit's structural `FilterRoute` contract; the route's own Zod
  // `validateSearch` keeps the boundary typed.
  const filterRoute: FilterRoute = {
    useSearch: () => Route.useSearch() as Record<string, unknown>,
    useNavigate: () => {
      const navigateRoute = Route.useNavigate();
      return (opts) =>
        navigateRoute({
          search: ((prev: Record<string, unknown>) => opts.search(prev)) as never,
          ...(opts.replace !== undefined ? { replace: opts.replace } : {}),
        });
    },
  };
  const filters = useListFilters(assessmentsFilterConfig, filterRoute, {
    pageSize: PAGE_SIZE,
  });
  const { values, apiParams, setSort, setPage } = filters;
  const tabs = scopeTabsForRole(role);
  const scopeFacet = values.facets.scope;
  const selectedScopes = scopeFacet?.kind === 'multi-select' ? scopeFacet.values : [];

  const authorScopes = useMemo<ReadonlyArray<AssessmentScope>>(
    () => ALL_SCOPES.filter((s) => canAuthorAtScope(role, s)),
    [role],
  );
  const canAuthorAny = authorScopes.length > 0;

  // The pencil shortcut mirrors the detail-page edit gate: the operator
  // must have author rights for the row's scope AND the template must
  // be in a status that still accepts edits (everything except archived,
  // matching `canEditTemplate`).
  const canManageRow = (row: TemplateListItem): boolean =>
    canAuthorAtScope(role, row.scope) && row.status !== 'archived';

  const listQuery = useQuery({
    queryKey: ['assessments', 'templates', apiParams] as const,
    queryFn: ({ signal }) =>
      apiRequest<ListResponse>('/api/assessments/templates', {
        signal,
        query: apiParams,
      }),
    placeholderData: (prev) => prev,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const columns = useMemo<ColumnDef<TemplateListItem, unknown>[]>(
    () => [
      {
        id: 'title',
        accessorKey: 'title',
        // The URL-backed `SortableHeader` is the single source of truth for
        // sorting; disable DataTable's built-in (client-side) column sort so it
        // doesn't add a competing handler + duplicate sort glyph on this header.
        enableSorting: false,
        header: () => (
          <SortableHeader
            label="Title"
            binding={ASSESSMENTS_SORT_COLUMNS.title}
            currentSort={values.sort}
            onSort={setSort}
          />
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void navigate({
                  to: '/academics/assessments/$templateId',
                  params: { templateId: row.id },
                });
              }}
              className="text-left font-medium text-[color:var(--color-text)] hover:text-gold-700"
            >
              {row.title}
            </button>
          );
        },
      },
      {
        id: 'scope',
        accessorKey: 'scope',
        enableSorting: false,
        header: 'Scope',
        cell: (info) => <ScopeChip scope={info.row.original.scope} />,
      },
      {
        id: 'mode',
        accessorKey: 'mode',
        enableSorting: false,
        header: 'Mode',
        cell: (info) => <ModeChip mode={info.row.original.mode} />,
      },
      {
        id: 'status',
        accessorKey: 'status',
        enableSorting: false,
        header: 'Status',
        cell: (info) => <StatusChip status={info.row.original.status} />,
      },
      {
        id: 'version',
        accessorKey: 'version',
        enableSorting: false,
        header: 'Ver',
        cell: (info) => (
          <span className="tabular-nums text-[color:var(--color-text-muted)]">
            v{info.row.original.version}
          </span>
        ),
      },
      {
        id: 'activeQuestionCount',
        enableSorting: false,
        header: 'Questions',
        cell: (info) => (
          <span className="tabular-nums">
            {info.row.original.activeQuestionCount}
            <span className="text-[color:var(--color-text-muted)]">
              {' '}/ {info.row.original.questionDrawCount}
            </span>
          </span>
        ),
      },
      {
        id: 'passThresholdPercent',
        enableSorting: false,
        header: 'Pass %',
        cell: (info) => (
          <span className="tabular-nums text-[color:var(--color-text-muted)]">
            {info.row.original.passThresholdPercent !== null
              ? `${info.row.original.passThresholdPercent}%`
              : '—'}
          </span>
        ),
      },
      {
        id: 'owner',
        enableSorting: false,
        header: 'Owner',
        cell: (info) => <OwnerTypeChip ownerType={info.row.original.ownerType} />,
      },
      {
        id: 'updatedAt',
        enableSorting: false,
        header: 'Updated',
        cell: (info) => (
          <span className="text-xs text-[color:var(--color-text-muted)]">
            {formatDate(info.row.original.updatedAt)}
          </span>
        ),
      },
      {
        id: 'edit',
        enableSorting: false,
        header: '',
        cell: (info) => {
          const row = info.row.original;
          if (!canManageRow(row)) return null;
          // The row body navigates; the pencil edits in place. The
          // EditIconButton stops propagation so the row click is not
          // also triggered.
          return (
            <div className="flex justify-end">
              <EditIconButton
                label="Edit template"
                onClick={() => setDrawer({ kind: 'edit', template: row })}
              />
            </div>
          );
        },
      },
    ],
    // `canManageRow` closes over `role`, so it is intentionally re-bound on
    // each render. The deps list mirrors what each cell renderer captures.
    [navigate, role, values.sort, setSort],
  );

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const isLoadingList = listQuery.isLoading && !listQuery.data;

  return (
    <ListOrOutlet basePath="/academics/assessments">
    <ListShell
      title="Tests & quizzes"
      description="Reusable templates for hiring tests, rules tests, weekly quizzes, monthly quizzes and interview scripts."
      primaryAction={
        canAuthorAny ? (
          <Button
            onClick={() => setScopePickerOpen(true)}
            icon={<Plus size={16} aria-hidden="true" />}
          >
            Create template
          </Button>
        ) : null
      }
    >
      {tabs.length > 1 ? (
        <div role="group" aria-label="Kind of test" className="mb-3 flex flex-wrap items-center gap-2">
          {[null, ...tabs].map((scope) => {
            const active = scope === null ? selectedScopes.length === 0 : selectedScopes.length === 1 && selectedScopes[0] === scope;
            return (
              <button
                key={scope ?? 'all'}
                type="button"
                aria-pressed={active}
                onClick={() => filters.setFacet('scope', { kind: 'multi-select', values: scope === null ? [] : [scope] })}
                className={clsx(
                  'inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                  active
                    ? 'border-gold-500 bg-gold-50 text-gold-700'
                    : 'border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] text-[color:var(--color-text-muted)] hover:border-gold-500 hover:text-[color:var(--color-text)]',
                )}
              >
                {scope === null ? 'All' : SCOPE_TAB_LABEL[scope]}
              </button>
            );
          })}
        </div>
      ) : null}

      <FilterBar config={assessmentsFilterConfig} filters={filters} resultCount={total} />

      <GatedSection
        error={listQuery.error}
        title="Tests & quizzes"
        onRetry={() => listQuery.refetch()}
      >
        <DataTable<TemplateListItem>
          data={items}
          columns={columns}
          isLoading={isLoadingList}
          getRowId={(row) => row.id}
          onRowClick={(row) =>
            void navigate({
              to: '/academics/assessments/$templateId',
              params: { templateId: row.id },
            })
          }
          emptyState={
            <EmptyState
              title="No templates match these filters"
              description={
                canAuthorAny
                  ? 'Try clearing a filter, or create your first template to start writing questions and running tests.'
                  : 'Once an author publishes a template, it will appear here.'
              }
              action={
                canAuthorAny ? (
                  <Button
                    onClick={() => setScopePickerOpen(true)}
                    icon={<Plus size={16} aria-hidden="true" />}
                  >
                    Create template
                  </Button>
                ) : null
              }
            />
          }
        />
        <Pagination
          page={values.page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      </GatedSection>

      <ScopePicker
        open={scopePickerOpen}
        onClose={() => setScopePickerOpen(false)}
        allowedScopes={authorScopes}
        onPick={(scope) => {
          setScopePickerOpen(false);
          setDrawer({ kind: 'create', scope });
        }}
      />

      <TemplateDrawer
        mode={drawer}
        onClose={() => setDrawer(null)}
        onSaved={(templateId) => {
          setDrawer(null);
          void queryClient.invalidateQueries({
            queryKey: ['assessments', 'templates'],
          });
          void navigate({
            to: '/academics/assessments/$templateId',
            params: { templateId },
          });
        }}
      />
    </ListShell>
    </ListOrOutlet>
  );
}
